import { useState } from 'react';
import canteiroAreiaService, { CanteiroAreiaAnalise } from '../../../../services/sementes/canteiroAreia';

interface UseCanteiroAreiaAnaliseResult {
  dadosAnalise: CanteiroAreiaAnalise | null;
  loading: boolean;
  error: string | null;
  submitAnalise: (
    id: string,
    dados: CanteiroAreiaAnalise,
    nAmostra?: string,
    onSuccess?: () => void,
    concluir?: boolean
  ) => Promise<void>;
  modalAnaliseConcluidaVisible: boolean;
  modalConfirmarConclusaoVisible: boolean;
  nAmostraAtual: string | null;
  fecharModalAnaliseConcluida: () => void;
  confirmarConclusao: () => Promise<void>;
  cancelarConclusao: () => void;
}

export default function useCanteiroAreiaAnalise(): UseCanteiroAreiaAnaliseResult {
  const [dadosAnalise, setDadosAnalise] = useState<CanteiroAreiaAnalise | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalAnaliseConcluidaVisible, setModalAnaliseConcluidaVisible] = useState(false);
  const [modalConfirmarConclusaoVisible, setModalConfirmarConclusaoVisible] = useState(false);
  const [nAmostraAtual, setNAmostraAtual] = useState<string | null>(null);
  const [pendingSubmit, setPendingSubmit] = useState<{
    id: string;
    dados: CanteiroAreiaAnalise;
    nAmostra?: string;
    onSuccess?:() => void;
      } | null>(null);

  const submitAnalise = async (
    id: string,
    dados: CanteiroAreiaAnalise,
    nAmostra?: string,
    onSuccess?: () => void,
    concluir = false,
  ) => {
    if (concluir) {
      // Mostrar modal de confirmação antes de concluir
      setPendingSubmit({
        id, dados, nAmostra, onSuccess,
      });
      setNAmostraAtual(nAmostra || null);
      setModalConfirmarConclusaoVisible(true);
      return;
    }

    // Salvar sem concluir
    setLoading(true);
    setError(null);

    try {
      await canteiroAreiaService.submitAnalise(id, dados);
      setDadosAnalise(dados);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao submeter análise');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const confirmarConclusao = async () => {
    if (!pendingSubmit) return;

    setModalConfirmarConclusaoVisible(false);
    setLoading(true);
    setError(null);

    try {
      await canteiroAreiaService.submitAnalise(pendingSubmit.id, pendingSubmit.dados);
      await canteiroAreiaService.concluirAnalise(pendingSubmit.id);

      setDadosAnalise(pendingSubmit.dados);

      if (pendingSubmit.onSuccess) {
        pendingSubmit.onSuccess();
      }

      setModalAnaliseConcluidaVisible(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao submeter análise');
      throw err;
    } finally {
      setLoading(false);
      setPendingSubmit(null);
    }
  };

  const cancelarConclusao = () => {
    setModalConfirmarConclusaoVisible(false);
    setPendingSubmit(null);
    setNAmostraAtual(null);
  };

  const fecharModalAnaliseConcluida = () => {
    setModalAnaliseConcluidaVisible(false);
    setNAmostraAtual(null);
  };

  return {
    dadosAnalise,
    loading,
    error,
    submitAnalise,
    modalAnaliseConcluidaVisible,
    modalConfirmarConclusaoVisible,
    nAmostraAtual,
    fecharModalAnaliseConcluida,
    confirmarConclusao,
    cancelarConclusao,
  };
}
